import { useEffect, useMemo, useState } from "react";
import { LegalFooter } from "@/components/legal/LegalFooter";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft, BedDouble, Ruler, Car, MapPin, Sparkles, ChevronRight, Award,
  Sun, Building2, Train, Trees, Camera, Play, ChevronDown, Share2, Phone, CalendarClock,
} from "lucide-react";
import { formatBRL } from "@/lib/format";
import { usePropertyList } from "@/hooks/use-properties";
import { photosFor, coverFor } from "@/lib/property-media";
import { SaveButton } from "@/components/nor/SaveButton";
import { LeadDialog } from "@/components/nor/LeadDialog";
import { ResumeChatFab } from "@/components/nor/ResumeChatFab";
import { NorChatDock } from "@/components/nor/NorChatDock";
import { useDwellTracker, useLandingCapture } from "@/hooks/use-telemetry";
import { useEvents } from "@/hooks/use-events";
import { useAuth } from "@/hooks/use-auth";
import { qualityScore, type QualityBand } from "@/lib/quality-score";
import { getPublicListingBySlug } from "@/lib/portal-catalog.functions";
import { toPropertyShape } from "@/lib/portal-adapter";
import { promptIdentify, isIdentified } from "@/lib/identify-gate";
import { toast } from "sonner";
import { VerifiedBrokerCard } from "@/components/nor/property/VerifiedBrokerCard";
import { PortalProBar } from "@/components/nor/PortalProBar";

const QUALITY_TONE: Record<QualityBand, string> = {
  excepcional: "border-accent/60 bg-accent/15 text-accent",
  raro: "border-accent/40 bg-accent/10 text-accent",
  sólido: "border-border bg-surface/60 text-foreground",
  regular: "border-border bg-surface/40 text-muted-foreground",
};

export const Route = createFileRoute("/imovel/$slug")({
  loader: async ({ params }) => {
    // Tenta listing real publicado primeiro; cai no mock se ainda não estiver no banco.
    try {
      const res = (await getPublicListingBySlug({ data: { slug: params.slug } })) as
        | { listing: Parameters<typeof toPropertyShape>[0] | null; og_image?: string | null }
        | undefined;
      if (res?.listing)
        return {
          property: toPropertyShape(res.listing),
          isReal: true,
          ogImage: res.og_image ?? null,
        };
    } catch {
      // falha de leitura vira 404 — nunca card vazio
    }
    throw notFound();
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.property;
    if (!p) return { meta: [{ title: "Imóvel não encontrado · NOR" }] };
    const title = `${p.title} · ${p.bairro}, ${p.cidade} · ${formatBRL(p.preco)} · NOR`;
    const desc = `${p.tipo} ${p.dorms} dorms · ${p.area}m² · ${p.bairro}. ${p.why[0] ?? ""} Curadoria NOR.`.slice(0, 158);
    // Prévia de link (WhatsApp): usa a capa assinada com TTL de 7 dias.
    const image = loaderData?.ogImage ?? coverFor(p);
    const url = `https://nor.run/imovel/${params.slug}`;
    const absImage = image?.startsWith("http") ? image : `https://nor.run${image}`;
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: absImage },
        { property: "og:locale", content: "pt_BR" },
        { property: "product:price:amount", content: String(p.preco) },
        { property: "product:price:currency", content: "BRL" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: absImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Residence"],
          name: p.title,
          description: desc,
          image: absImage,
          url,
          category: p.tipo,
          sku: p.code ?? p.id,
          address: {
            "@type": "PostalAddress",
            streetAddress: p.bairro,
            addressLocality: p.cidade,
            addressRegion: (p as unknown as { __state?: string | null }).__state ?? "SP",
            addressCountry: "BR",
          },
          numberOfRooms: p.dorms,
          numberOfBedrooms: p.dorms,
          numberOfBathroomsTotal: (p as any).banheiros ?? undefined,
          floorSize: { "@type": "QuantitativeValue", value: p.area, unitCode: "MTK" },
          offers: {
            "@type": "Offer",
            price: p.preco,
            priceCurrency: "BRL",
            url,
            availability:
              p.status === "Disponível"
                ? "https://schema.org/InStock"
                : "https://schema.org/LimitedAvailability",
          },
        }),
      }, {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "NOR", item: "https://nor.run" },
            { "@type": "ListItem", position: 2, name: "Buscar", item: "https://nor.run/buscar" },
            { "@type": "ListItem", position: 3, name: p.bairro, item: `https://nor.run/buscar?bairro=${encodeURIComponent(p.bairro)}` },
            { "@type": "ListItem", position: 4, name: p.title, item: url },
          ],
        }),
      }],
    };
  },
  notFoundComponent: NotFoundProperty,
  component: PropertyPage,
});

const DEMO_NOTICE =
  "Imóvel de demonstração. O contato com corretor fica disponível nos imóveis publicados por imobiliárias no NOR.";

function NotFoundProperty() {
  return (
    <div data-surface="portal" className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="max-w-md text-center">
        <div className="mb-2 text-[10px] uppercase tracking-[0.22em] text-accent">404 · imóvel</div>
        <h1 className="font-display text-3xl tracking-tight">Esse imóvel não está mais no portal.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Pode ter sido vendido, retirado ou nunca ter existido. Volte para a curadoria.
        </p>
        <Link to="/buscar" className="mt-6 inline-flex h-10 items-center gap-2 rounded-md bg-foreground px-4 text-[11px] font-medium uppercase tracking-widest text-background transition-opacity hover:opacity-90">
          <ArrowLeft className="h-3.5 w-3.5" /> voltar ao portal
        </Link>
      </div>
    </div>
  );
}

function PropertyPage() {
  const { property: p, isReal } = Route.useLoaderData();
  const photos = useMemo(() => photosFor(p), [p]);
  const [activePhoto, setActivePhoto] = useState(0);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadKind, setLeadKind] = useState<"falar" | "visita">("falar");
  const [scrolled, setScrolled] = useState(false);
  const quality = useMemo(() => qualityScore(p), [p]);
  const { data: catalog } = usePropertyList();
  const related = catalog.filter((x) => x.id !== p.id).slice(0, 3);
  const { track } = useEvents();
  const { user } = useAuth();

  useEffect(() => { track("view", { property_slug: p.id }); }, [p.id, track]);
  useDwellTracker(p.id);
  useLandingCapture();

  // Visita: se anônimo e não identificado, dispara gate leve (cria lead com "Quero agendar uma visita").
  // Caso contrário, abre o LeadDialog completo (refina data/horário).
  function openVisit() {
    if (!isReal) return;
    if (!user && !isIdentified()) {
      const opened = promptIdentify({ reason: "schedule_visit", propertySlug: p.id });
      if (opened) {
        track("schedule_visit_intent", { property_slug: p.id, metadata: { gated: true } });
        return;
      }
    }
    setLeadKind("visita");
    setLeadOpen(true);
    track("schedule_visit_intent", { property_slug: p.id, metadata: { gated: false } });
  }

  async function handleShare() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const shareData = { title: p.title, text: `${p.title} · ${p.bairro}`, url };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado");
      }
      track("share", { property_slug: p.id });
    } catch {
      /* usuário cancelou share nativo */
    }
    if (!user && !isIdentified()) {
      promptIdentify({ reason: "share_collection", propertySlug: p.id });
    }
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <article data-surface="portal" className="min-h-screen bg-background text-foreground">
      <PortalProBar />
      {/* ===== HERO FULLSCREEN ===== */}
      <section className="relative h-screen w-full overflow-hidden bg-black">
        {photos.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt={`${p.title} — foto ${i + 1}`}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
              i === activePhoto ? "opacity-100" : "opacity-0"
            }`}
            style={{
              animation: i === activePhoto ? "kenburns 24s ease-in-out infinite alternate" : undefined,
            }}
          />
        ))}
        <style>{`@keyframes kenburns { 0% { transform: scale(1.06) translate(0,0); } 100% { transform: scale(1.14) translate(-1.5%, -1%); } }`}</style>

        {/* gradient overlays — fundo escuro apenas no rodapé pra preservar a foto */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

        {/* Top bar */}
        <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-5 md:px-10">
          <Link to="/buscar" className="flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/85 backdrop-blur transition-colors hover:bg-black/50">
            <ArrowLeft className="h-3.5 w-3.5" /> portal NOR
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full border border-white/15 bg-black/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/85 backdrop-blur sm:inline-flex">
              <span className="size-1.5 animate-soft-pulse rounded-full bg-accent" />
              {p.code} · {p.status}
            </span>
            <button
              type="button"
              onClick={handleShare}
              aria-label="Compartilhar imóvel"
              className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-black/30 text-white/85 backdrop-blur transition-colors hover:bg-black/50"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <div className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-black/30 text-white/85 backdrop-blur">
              <SaveButton propertySlug={p.id} variant="icon" />
            </div>
          </div>
        </header>

        {/* Center play (visual cue, sem vídeo real) */}
        <button
          onClick={() => setActivePhoto((i) => (i + 1) % photos.length)}
          className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 grid h-20 w-20 place-items-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-md transition-transform hover:scale-105"
          aria-label="Próxima foto / tour"
        >
          <Play className="h-7 w-7 translate-x-0.5" />
        </button>

        {/* Bottom info */}
        <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-10 md:px-10 md:pb-14">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="text-white">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                <span className="rounded-full border border-white/25 px-2.5 py-0.5">{p.tipo}</span>
                <span>· {p.bairro}, {p.cidade}</span>
                <span className="hidden items-center gap-1.5 rounded-full border border-accent/60 bg-accent/15 px-2.5 py-0.5 text-accent sm:inline-flex">
                  <Sparkles className="h-3 w-3" /> match {p.match_default}
                </span>
              </div>
              <h1 className="font-display text-4xl leading-[1.02] tracking-tight md:text-6xl lg:text-7xl">
                {p.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
                <span className="flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" />{p.area}m²</span>
                <span className="flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5" />{p.dorms} dorms</span>
                <span className="flex items-center gap-1.5"><Car className="h-3.5 w-3.5" />{p.vagas} vagas</span>
                <span className="flex items-center gap-1.5"><Sun className="h-3.5 w-3.5" />{p.orientacao}</span>
                <span className="flex items-center gap-1.5"><Train className="h-3.5 w-3.5" />metrô {p.proximidade.metro_m}m</span>
              </div>
            </div>

            <div className="flex flex-col items-start gap-3 md:items-end">
              <div className="font-display text-4xl text-white tabular-nums md:text-5xl">{formatBRL(p.preco)}</div>
              {isReal ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openVisit}
                    className="h-11 rounded-full border border-white/25 bg-white/10 px-5 text-xs font-medium uppercase tracking-widest text-white backdrop-blur transition-colors hover:bg-white/20"
                  >
                    <CalendarClock className="mr-2 inline h-3.5 w-3.5" /> agendar visita
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLeadKind("falar"); setLeadOpen(true); }}
                    className="h-11 rounded-full bg-accent px-5 text-xs font-medium uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
                  >
                    <Phone className="mr-2 inline h-3.5 w-3.5" /> falar com corretor
                  </button>
                </div>
              ) : (
                <div className="max-w-sm rounded-lg border border-white/20 bg-black/40 px-4 py-3 text-xs leading-relaxed text-white/80 backdrop-blur">
                  {DEMO_NOTICE}
                </div>
              )}
            </div>
          </div>

          {/* photo strip */}
          <div className="mx-auto mt-8 flex max-w-7xl items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {photos.map((src, i) => (
              <button
                key={src + i}
                onClick={() => setActivePhoto(i)}
                className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border transition ${
                  i === activePhoto ? "border-accent ring-2 ring-accent/40" : "border-white/20 opacity-70 hover:opacity-100"
                }`}
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
            <span className="ml-2 hidden items-center gap-1.5 rounded-md border border-white/15 bg-black/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/70 backdrop-blur md:inline-flex">
              <Camera className="h-3 w-3" /> {photos.length} fotos · tour ao vivo
            </span>
          </div>

          <div className="mx-auto mt-6 flex max-w-7xl items-center justify-center text-white/60">
            <ChevronDown className="h-5 w-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ===== STICKY CTA BAR (after hero scrolled) ===== */}
      <div
        className={`fixed inset-x-0 top-0 z-30 border-b border-border bg-background/95 backdrop-blur transition-transform duration-300 ${
          scrolled ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/buscar" className="text-muted-foreground transition-colors hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{p.title}</div>
              <div className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">{p.bairro} · {p.tipo}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-display text-lg tabular-nums md:inline">{formatBRL(p.preco)}</span>
            {isReal ? (
              <>
                <button
                  onClick={openVisit}
                  className="hidden h-9 rounded-md border border-border px-3 text-[11px] uppercase tracking-widest text-foreground transition-colors hover:border-border-strong sm:inline-block"
                >
                  agendar
                </button>
                <button
                  onClick={() => { setLeadKind("falar"); setLeadOpen(true); }}
                  className="h-9 rounded-md bg-accent px-4 text-[11px] font-medium uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
                >
                  falar com corretor
                </button>
              </>
            ) : (
              <span className="hidden text-[10px] uppercase tracking-widest text-muted-foreground md:inline">
                imóvel de demonstração
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
        {/* Lead intelligence row */}
        <div className="mb-12 grid gap-4 md:grid-cols-3">
          <KpiCard icon={Sparkles} label="match com seu perfil" value={`${p.match_default}`} hint="curadoria NOR · 13 agentes" tone="accent" />
          <KpiCard icon={Award} label="selo de qualidade" value={`${quality.score}/100`} hint={quality.label} tone={quality.band === "excepcional" || quality.band === "raro" ? "accent" : "neutral"} />
          <KpiCard icon={Trees} label="sinais de intenção · 7d" value={`${p.intent_signals}`} hint={`${p.vistas} visualizações`} tone="neutral" />
        </div>

        <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr]">
          <div className="space-y-14">
            {/* NOR intelligence */}
            <section>
              <SectionLabel>Inteligência NOR · por que esse imóvel</SectionLabel>
              <div className="mt-5 grid gap-3">
                {p.why.map((w: string, i: number) => (
                  <div key={i} className="flex items-start gap-4 rounded-xl border border-accent/25 bg-gradient-to-br from-accent/[0.08] via-accent/[0.02] to-transparent p-5">
                    <span className="mt-1.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-accent/40 bg-background text-[10px] font-medium text-accent">
                      {i + 1}
                    </span>
                    <span className="text-base leading-relaxed">{w}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Selo de qualidade */}
            <section>
              <SectionLabel>Selo de qualidade NOR</SectionLabel>
              <div className={`mt-5 rounded-2xl border p-6 ${QUALITY_TONE[quality.band]}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-5xl tabular-nums">{quality.score}</span>
                    <span className="text-[11px] uppercase tracking-[0.22em] opacity-80">/ 100 · {quality.label}</span>
                  </div>
                  <Award className="h-6 w-6 opacity-70" />
                </div>
                {quality.reasons.length > 0 && (
                  <ul className="mt-5 grid gap-2 text-[12px] sm:grid-cols-2">
                    {quality.reasons.map((r: string) => (
                      <li key={r} className="flex items-center gap-2">
                        <span className="size-1 rounded-full bg-current opacity-70" /> {r}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-4 text-[11px] opacity-70">
                  Score editorial NOR — combina luz, andar, pé-direito, suítes e localização. Independente do seu match.
                </p>
              </div>
            </section>

            {/* Ficha técnica */}
            <section>
              <SectionLabel>Ficha técnica</SectionLabel>
              <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-5 border-y border-border py-6 sm:grid-cols-4">
                <Spec k="área" v={`${p.area}m²`} />
                <Spec k="dormitórios" v={`${p.dorms}`} />
                <Spec k="suítes" v={`${p.suites}`} />
                <Spec k="vagas" v={`${p.vagas}`} />
                <Spec k="andar" v={`${p.andar}`} />
                <Spec k="pé direito" v={`${p.pe_direito}m`} />
                <Spec k="orientação" v={p.orientacao} />
                <Spec k="condomínio" v={`R$ ${p.condominio.toLocaleString("pt-BR")}`} />
              </div>
            </section>

            {/* Highlights */}
            <section>
              <SectionLabel>Destaques</SectionLabel>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.highlights.map((h: string) => (
                  <span key={h} className="rounded-full border border-border bg-surface/60 px-3.5 py-1.5 text-xs">{h}</span>
                ))}
              </div>
            </section>

            {/* Proximidade */}
            <section>
              <SectionLabel>Localização & vizinhança</SectionLabel>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <ProxCard icon={Train} label="metrô mais próximo" value={`${p.proximidade.metro_m}m`} />
                <ProxCard icon={Building2} label="escolas em 1km" value={`${p.proximidade.escolas}`} />
                <ProxCard icon={Trees} label="parque mais próximo" value={`${p.proximidade.parque_m}m`} />
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-surface/40 p-4 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 text-accent" />
                {p.bairro}, {p.cidade} · coordenadas {p.geo.lat.toFixed(4)}, {p.geo.lng.toFixed(4)}
              </div>
            </section>
          </div>

          {/* Sticky sidebar */}
          <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
            {(() => {
              const meta = p as unknown as { __broker_slug?: string | null; __broker_avatar?: string | null };
              if (meta.__broker_slug) {
                return (
                  <VerifiedBrokerCard
                    brokerSlug={meta.__broker_slug}
                    fallbackName={p.agente.nome}
                    fallbackInitials={p.agente.foto_iniciais}
                    fallbackAvatar={meta.__broker_avatar ?? null}
                    onContact={() => { setLeadKind("falar"); setLeadOpen(true); }}
                    onSchedule={openVisit}
                  />
                );
              }
              if (!isReal) {
                return (
                  <div className="rounded-2xl border border-border bg-surface/40 p-6">
                    <div className="mb-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      Imóvel de demonstração
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">{DEMO_NOTICE}</p>
                    <Link
                      to="/buscar"
                      className="mt-5 inline-flex h-10 items-center justify-center rounded-md border border-border px-4 text-[11px] uppercase tracking-widest text-foreground transition-colors hover:border-border-strong"
                    >
                      ver imóveis publicados
                    </Link>
                  </div>
                );
              }
              return (
                <div className="rounded-2xl border border-border bg-surface/40 p-6">
                  <div className="mb-4 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Corretor responsável</div>
                  <div className="flex items-center gap-3">
                    <div className="grid size-12 place-items-center rounded-full border border-accent/40 bg-accent/10 text-sm font-medium text-accent">
                      {p.agente.foto_iniciais}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{p.agente.nome}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground tabular-nums">
                        ★ {p.agente.rating} · {p.agente.deals} fechados
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 grid gap-2">
                    <button
                      type="button"
                      onClick={() => { setLeadKind("falar"); setLeadOpen(true); }}
                      className="h-11 rounded-md bg-accent text-[11px] font-medium uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
                    >
                      Falar com {p.agente.nome.split(" ")[0]}
                    </button>
                    <button
                      type="button"
                      onClick={openVisit}
                      className="h-11 rounded-md border border-border bg-surface text-[11px] uppercase tracking-widest text-foreground transition-colors hover:border-border-strong"
                    >
                      Agendar visita
                    </button>
                  </div>
                </div>
              );
            })()}

            <Link
              to="/login"
              className="flex items-center justify-between rounded-2xl border border-accent/30 bg-gradient-to-b from-accent/15 to-transparent p-5 text-sm transition-colors hover:border-accent/50"
            >
              <span>
                <span className="block font-medium">Salvar em coleção</span>
                <span className="block text-[11px] text-muted-foreground">Entrar leva 10s</span>
              </span>
              <ChevronRight className="h-4 w-4 text-accent" />
            </Link>
          </aside>
        </div>

        {/* Relacionados */}
        {related.length > 0 && (
          <section className="mt-24 border-t border-border pt-12">
            <SectionLabel>Curadoria relacionada</SectionLabel>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to="/imovel/$slug"
                  params={{ slug: r.id }}
                  className="group overflow-hidden rounded-xl border border-border bg-surface/40 transition-colors hover:border-accent/40"
                >
                  <div className="relative aspect-[5/3] overflow-hidden">
                    <img src={coverFor(r)} alt={r.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-accent">{r.tipo} · {r.bairro}</div>
                    <div className="mt-1 text-sm font-medium leading-snug">{r.title}</div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                      <span>{formatBRL(r.preco)}</span>
                      <span>{r.area}m² · {r.dorms} dorms</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <LeadDialog
        open={isReal && leadOpen}
        onClose={() => setLeadOpen(false)}
        propertySlug={p.id}
        source="pdp"
        agentName={p.agente.nome}
        conversation={leadKind === "visita" ? [{ role: "user", content: "Quero agendar uma visita." }] : undefined}
      />
      <ResumeChatFab />
      <NorChatDock property={p} />
      <LegalFooter />
    </article>

  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[10px] uppercase tracking-[0.22em] text-accent">{children}</h2>;
}

function Spec({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="mt-1 font-display text-xl tabular-nums">{v}</div>
    </div>
  );
}

function ProxCard({ icon: Icon, label, value }: { icon: typeof Train; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface/40 p-5">
      <Icon className="h-4 w-4 text-accent" />
      <div className="mt-3 font-display text-2xl tabular-nums">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

function KpiCard({
  icon: Icon, label, value, hint, tone,
}: { icon: typeof Sparkles; label: string; value: string; hint: string; tone: "accent" | "neutral" }) {
  return (
    <div className={`rounded-2xl border p-5 ${tone === "accent" ? "border-accent/30 bg-accent/[0.06]" : "border-border bg-surface/40"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${tone === "accent" ? "text-accent" : "text-muted-foreground"}`} />
      </div>
      <div className={`mt-3 font-display text-3xl tabular-nums ${tone === "accent" ? "text-accent" : ""}`}>{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}
